import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatContext } from '../../context/ChatContext';
import { FiEdit2, FiTrash2, FiCheck, FiX, FiCornerUpLeft, FiMapPin } from 'react-icons/fi';
import { useToast } from '../commons/toast/ToastContext';

// TODO: Re-enable emoji reactions - needs proper UI/UX design
// const EMOJI_OPTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const MessageBubble = ({ message, isOwn, showSender, userId, onReply, showReadReceipt }) => {
    const { t } = useTranslation('chat');
    const { editMessage, deleteMessage, pinMessage, unpinMessage } = useContext(ChatContext);
    const toast = useToast();

    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState('');
    const [showActions, setShowActions] = useState(false);

    const isDeleted = message.status === 'DELETED';
    const isEdited = message.status === 'EDITED';
    const isSystem = message.messageType === 'SYSTEM';

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    };

    const handleEdit = () => {
        setEditContent(message.content);
        setIsEditing(true);
        setShowActions(false);
    };

    const handleSaveEdit = async () => {
        if (!editContent.trim()) return;
        try {
            await editMessage(message.id, editContent.trim());
            setIsEditing(false);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteMessage(message.id);
            setShowActions(false);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleReply = () => {
        if (onReply) {
            onReply(message);
        }
        setShowActions(false);
    };

    // TODO: Re-enable emoji reactions - needs proper UI/UX design
    // const handleReactionClick = (emoji) => { ... };
    // const handleEmojiPickerSelect = (emoji) => { ... };

    const handlePin = async () => {
        try {
            if (message.pinned) {
                await unpinMessage(message.id);
                toast.success(t('actions.unpinSuccess'));
            } else {
                await pinMessage(message.id);
                toast.success(t('actions.pinSuccess'));
            }
            setShowActions(false);
        } catch (err) {
            toast.error(err.message);
        }
    };

    if (isSystem) {
        return (
            <div className="chat-system-message">
                <span>{message.content}</span>
            </div>
        );
    }

    return (
        <div
            className={`chat-message message-bubble-wrapper ${isOwn ? 'chat-message-own' : 'chat-message-other'}`}
            onMouseEnter={() => !isDeleted && setShowActions(true)}
            onMouseLeave={() => setShowActions(false)}
        >
            {showSender && !isOwn && (
                <span className="chat-message-sender">
                    {message.senderFirstName} {message.senderLastName}
                </span>
            )}

            {/* Reply reference */}
            {message.parentMessageId && (
                <div className="message-reply-ref">
                    <div className="message-reply-ref-sender">
                        {message.parentMessageSenderName || t('user')}
                    </div>
                    <div className="message-reply-ref-content">
                        {message.parentMessageContent
                            ? (message.parentMessageContent.length > 80
                                ? message.parentMessageContent.substring(0, 80) + '...'
                                : message.parentMessageContent)
                            : ''}
                    </div>
                </div>
            )}

            <div className={`chat-bubble ${isDeleted ? 'chat-bubble-deleted' : ''}`}>
                {isEditing ? (
                    <div className="chat-edit-area">
                        <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="chat-edit-input"
                            autoFocus
                        />
                        <div className="chat-edit-actions">
                            <button onClick={handleSaveEdit} className="chat-edit-save">
                                <FiCheck />
                            </button>
                            <button onClick={() => setIsEditing(false)} className="chat-edit-cancel">
                                <FiX />
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <p className="chat-message-content">
                            {isDeleted ? t('messageDeleted') : message.content}
                        </p>
                        <div className="chat-message-meta">
                            <span className="chat-message-time">{formatTime(message.createdAt)}</span>
                            {isEdited && !isDeleted && (
                                <span className="chat-message-edited">{t('edited')}</span>
                            )}
                            {message.pinned && (
                                <span className="message-pin-indicator">📌</span>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* TODO: Re-enable emoji reactions UI here */}

            {/* Read receipt */}
            {isOwn && showReadReceipt && (
                <div className="message-read-receipt">{t('readReceipt')}</div>
            )}

            {showActions && !isEditing && !isDeleted && (
                <div className="chat-message-actions">
                    <button onClick={handleReply} title={t('actions.reply')}>
                        <FiCornerUpLeft />
                    </button>
                    <button onClick={handlePin} title={message.pinned ? t('actions.unpin') : t('actions.pin')}>
                        <FiMapPin />
                    </button>
                    {isOwn && (
                        <>
                            <button onClick={handleEdit} title={t('actions.editMessage')}>
                                <FiEdit2 />
                            </button>
                            <button onClick={handleDelete} title={t('actions.deleteMessage')}>
                                <FiTrash2 />
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default MessageBubble;
